import { useState, useEffect, useRef } from 'react';

export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// Hook for debounced search with request cancellation
export function useDebouncedSearch(
  searchTerm: string,
  delay: number = 350
) {
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    // Cancel the previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create a new AbortController for this request
    abortControllerRef.current = new AbortController();

    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, delay);

    return () => {
      clearTimeout(timer);
      // Don't abort here as we want the current request to continue
    };
  }, [searchTerm, delay]);

  // Function to get the current AbortController signal
  const getAbortSignal = () => {
    if (abortControllerRef.current) {
      return abortControllerRef.current.signal;
    }
    return null;
  };

  return {
    debouncedSearchTerm,
    getAbortSignal
  };
}